import type { ReactNode } from 'react';
import { Suspense, useState, useTransition } from 'react';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { isScrollNearBottom } from '@gonfalon/dom/isScrollNearBottom';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { getInfiniteTagsQuery } from '@gonfalon/rest-api/queries/tags';
import { convertToKey } from '@gonfalon/strings/convertToKey';
import { Autocomplete } from '@gonfalon/ui/Autocomplete';
import type { AutocompleteProps, ListBoxProps } from '@launchpad-ui/components';
import { ListBox, ListBoxItem } from '@launchpad-ui/components';
import type { MultipleSelection } from '@react-types/shared';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';

import { CreateTagFooter } from '../internal/components/CreateTagFooter';
import { RemoveAllTagsFooter } from '../internal/components/RemoveAllTagsFooter';
import { validateTag } from '../validation';

import { TagSearchField } from './internal/TagSearchField';
import type { TagKind } from './types';

import styles from './TagAutocomplete.module.css';

const maxNumberOfTags = 20;
const debounceTimeoutMillis = 100;

interface TagItem {
  id: string;
  name: string;
}

type FooterType = 'none' | 'remove-all' | 'create-tag';

interface TagSelectionDialogProps
  extends
    Omit<AutocompleteProps, 'children' | 'filter'>,
    Pick<MultipleSelection, 'selectedKeys' | 'onSelectionChange'> {
  kind?: TagKind;
  fullWidth?: boolean;
  allowCreate?: boolean;
  children: (item: { id: string; name: string }) => ReactNode;
}

export function TagAutocomplete({
  fullWidth = false,
  kind,
  children,
  selectedKeys = [],
  onSelectionChange,
  allowCreate = false,
  ...props
}: TagSelectionDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState(props.defaultInputValue ?? props.inputValue ?? '');

  return (
    <Autocomplete
      {...props}
      defaultInputValue={filterText}
      onInputChange={(nextFilter) => startTransition(() => setFilterText(nextFilter))}
    >
      <TagSearchField isPending={isPending} />

      <QuietErrorBoundary>
        <Suspense
          fallback={
            <div className={styles.fallback}>
              <div className={styles.item} />
              <div className={styles.item} />
              <div className={styles.item} />
              <div className={styles.item} />
            </div>
          }
        >
          <TagListBox
            fullWidth={fullWidth}
            kind={kind}
            filterText={filterText}
            allowCreate={allowCreate}
            escapeKeyBehavior="none"
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            onFilterTextChange={setFilterText}
          >
            {children}
          </TagListBox>
        </Suspense>
      </QuietErrorBoundary>
    </Autocomplete>
  );
}

interface TagMenuProps extends ListBoxProps<TagItem> {
  kind?: TagKind;
  filterText: string;
  allowCreate?: boolean;
  fullWidth?: boolean;
  onFilterTextChange?: (value: string) => void;
}

function TagListBox({ kind, filterText, allowCreate = false, fullWidth, onFilterTextChange, ...props }: TagMenuProps) {
  const { data, fetchNextPage } = useSuspenseInfiniteQuery({
    ...getInfiniteTagsQuery({
      query: {
        pre: convertToKey(filterText),
        kind,
        offset: 0,
        limit: maxNumberOfTags,
      },
    }),
  });

  let selectedTags: string[] = [];
  if (props.selectedKeys) {
    if (props.selectedKeys === 'all') {
      selectedTags = [];
    } else {
      selectedTags = Array.from(props.selectedKeys).map((key) => String(key));
    }
  }

  const selectedItems = selectedTags.map((tag) => ({ id: tag, name: tag }));

  const tags = data.pages.map((page) => page.items.map((item) => item)).flat();
  const items = tags.map((item) => ({ id: item, name: item }));

  const shouldShowCreateInFooter = Boolean(filterText && allowCreate && !tags.includes(filterText));
  const hasSelectedTags = selectedTags.length > 0;

  const footerType: FooterType =
    shouldShowCreateInFooter && allowCreate ? 'create-tag' : hasSelectedTags ? 'remove-all' : 'none';

  let accessibleItems: TagItem[];
  // If there is a filter text, show all items
  if (filterText) {
    accessibleItems = items;

    // Never add the create option to the list - it should always go to footer when needed
  } else {
    // Otherwise, bump selected items to the top of other available items, limit to 5
    const displayedSelectedItems = selectedItems.slice(0, maxNumberOfTags);
    accessibleItems = [...displayedSelectedItems, ...items.filter((item) => !selectedTags.includes(item.id))];
  }

  return (
    <>
      <ListBox
        {...props}
        className={fullWidth ? styles.fullWidthListBox : styles.listbox}
        selectionMode="multiple"
        items={accessibleItems}
        renderEmptyState={() => (
          <span className={styles.emptyState}>
            <Message id="ui.tagPicker.noTagsFound" />
          </span>
        )}
        onScroll={useDebounce((event) => {
          if (isScrollNearBottom(event)) {
            void fetchNextPage();
          }
        }, debounceTimeoutMillis)}
      >
        {(item) => (
          <ListBoxItem id={item.id} className={fullWidth ? styles.fullWidthListBoxItem : styles.listBoxItem}>
            {item.name}
          </ListBoxItem>
        )}
      </ListBox>

      {footerType === 'create-tag' && (
        <CreateTagFooter
          filterText={filterText}
          onPress={() => {
            const validation = validateTag(filterText);
            if (!validation.isValid) {
              return;
            }

            if (props.onSelectionChange && props.selectedKeys) {
              const newSelection = new Set(props.selectedKeys === 'all' ? [] : Array.from(props.selectedKeys));
              newSelection.add(filterText.trimEnd());
              props.onSelectionChange(newSelection);
              onFilterTextChange?.('');
            }
          }}
        />
      )}

      {footerType === 'remove-all' && <RemoveAllTagsFooter onPress={() => props.onSelectionChange?.(new Set())} />}
    </>
  );
}
